import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { MediaImg } from "./MediaImg";
import { CaseStudyModal } from "./CaseStudyModal";

const RADIUS = 195;
const SIZE = 500;

export const SpinWheel = ({ projects }) => {
  const N = projects.length;
  const step = 360 / N;
  const [rotation, setRotation] = useState(0);
  const [openProject, setOpenProject] = useState(null);
  const active = ((-Math.round(rotation / step)) % N + N) % N;
  const current = projects[active];

  const goTo = (i) => {
    const target = 360 * Math.round((rotation + i * step) / 360) - i * step;
    setRotation(target);
  };

  return (
    <div className="grid items-center gap-12 lg:grid-cols-2">
      <div
        className="relative mx-auto origin-center scale-[0.62] sm:scale-90 xl:scale-100"
        style={{ width: SIZE, height: SIZE }}
        data-testid="spin-wheel"
      >
        <div className="absolute inset-6 rounded-full border border-ink/10" />
        <div className="absolute inset-0 rounded-full border border-gold/25" />
        <span className="absolute left-1/2 top-0 h-4 w-[2px] -translate-x-1/2 bg-gold" />

        <button
          data-testid="spin-wheel-open"
          onClick={() => setOpenProject(current)}
          aria-label={`Open ${current.title} case study`}
          className="group absolute left-1/2 top-1/2 h-56 w-56 -translate-x-1/2 -translate-y-1/2 cursor-pointer overflow-hidden rounded-full border-4 border-sand shadow-2xl"
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={current.id}
              initial={{ opacity: 0, scale: 1.08 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="h-full w-full"
            >
              <MediaImg src={current.image} alt={current.title} className="h-full w-full object-cover" />
            </motion.div>
          </AnimatePresence>
          <span className="absolute inset-0 flex items-center justify-center bg-night/45 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
            <span className="border border-sand/50 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-sand">
              View case
            </span>
          </span>
        </button>

        {projects.map((p, i) => {
          const angle = i * step + rotation;
          const isActive = i === active;
          return (
            <button
              key={p.id}
              data-testid={`spin-wheel-item-${i}`}
              onClick={() => (i === active ? setOpenProject(current) : goTo(i))}
              aria-label={i === active ? `Open ${p.title} case study` : `Show ${p.title}`}
              className="absolute left-1/2 top-1/2 h-24 w-24 cursor-pointer overflow-hidden rounded-full border-2 shadow-lg"
              style={{
                marginLeft: -48,
                marginTop: -48,
                transform: `rotate(${angle}deg) translateY(-${RADIUS}px) rotate(${-angle}deg) scale(${
                  isActive ? 1.18 : 0.82
                })`,
                borderColor: isActive ? "#C5A059" : "rgba(18,19,22,0.15)",
                opacity: isActive ? 1 : 0.75,
                transition: "transform 0.85s cubic-bezier(0.22,1,0.36,1), border-color 0.4s, opacity 0.4s",
                zIndex: isActive ? 10 : 1,
              }}
            >
              <MediaImg src={p.image} alt="" className="h-full w-full object-cover" />
            </button>
          );
        })}
      </div>

      <div data-testid="spin-wheel-active-panel">
        <AnimatePresence mode="wait">
          <motion.div
            key={current.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold-dark">
              {current.category} · {current.location}
            </p>
            <h3 className="mt-3 font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl">
              {current.title}
            </h3>
            <p className="mt-4 max-w-lg leading-relaxed text-smoke">{current.summary}</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {current.tags.map((t) => (
                <span
                  key={t}
                  className="border border-ink/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-ink/70"
                >
                  {t}
                </span>
              ))}
            </div>
            <Link
              to="/work"
              data-testid="spin-wheel-view-work"
              className="group mt-7 inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-[0.18em] text-ink transition-colors duration-300 hover:text-gold-dark"
            >
              Explore the work
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </motion.div>
        </AnimatePresence>

        <div className="mt-9 flex items-center gap-3">
          <button
            data-testid="spin-wheel-prev"
            onClick={() => setRotation((r) => r + step)}
            aria-label="Previous project"
            className="flex h-12 w-12 items-center justify-center border border-ink/20 text-ink transition-colors duration-300 hover:border-gold hover:bg-gold hover:text-ink"
          >
            <ArrowLeft size={18} />
          </button>
          <button
            data-testid="spin-wheel-next"
            onClick={() => setRotation((r) => r - step)}
            aria-label="Next project"
            className="flex h-12 w-12 items-center justify-center border border-ink/20 text-ink transition-colors duration-300 hover:border-gold hover:bg-gold hover:text-ink"
          >
            <ArrowRight size={18} />
          </button>
          <span className="ml-3 font-mono text-xs tracking-[0.3em] text-ink/40">
            {String(active + 1).padStart(2, "0")} / {String(N).padStart(2, "0")}
          </span>
        </div>

        <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-ink/35">
          Tip — click the center image to open the project
        </p>
      </div>

      <AnimatePresence>
        {openProject && <CaseStudyModal project={openProject} onClose={() => setOpenProject(null)} />}
      </AnimatePresence>
    </div>
  );
};
